import { colors } from '@akin-home/theme';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Animated, PanResponder, View } from 'react-native';

interface OneDimensionJoystickProps {
  onChange: (value: number) => void;
  length: number;
  breadth: number;
  throttle?: number;
  stickyRate?: number;
  horizontal?: boolean;
  disabled?: boolean;
}

export const OneDimensionJoystick = (props: OneDimensionJoystickProps) => {
  const pan = useRef(new Animated.Value(0)).current;
  const latestVal = useRef<number>(0);
  const [isActive, setIsActive] = useState<boolean>(false);

  /**
   * Wrapper of props.onChange that handles throttling
   */
  const throttleTimer = useRef<any>(null);
  const handleOnChange = useCallback(
    (val: number) => {
      // eslint-disable-next-line eqeqeq
      if (val == undefined) {
        return;
      }

      if (props.length > 0) {
        val = val / props.length;
      } else {
        val = 0;
      }

      if (!props.horizontal) {
        // inverted due to conventions
        val = -val;
      }

      if (props.throttle) {
        // Check if the timeout since last call has passed. If it hasn't, then we don't do anything
        if (throttleTimer.current === null) {
          props.onChange(val);

          throttleTimer.current = setTimeout(() => {
            throttleTimer.current = null;
          }, props.throttle);
        }
      } else {
        props.onChange(val);
      }
    },
    [props]
  );

  /**
   * Handle sending data when throttle is held still at the sticky rate
   */
  useEffect(() => {
    if (props.stickyRate && isActive) {
      const id = setInterval(
        () => handleOnChange(latestVal.current),
        props.stickyRate
      );

      return () => clearInterval(id);
    }
  }, [handleOnChange, isActive, props.stickyRate]);

  /**
   * Handle adding listeners to the pan changes
   */
  useEffect(() => {
    const id = pan.addListener(({ value }) => {
      handleOnChange(value);
      latestVal.current = value;
    });

    return () => pan.removeListener(id);
  }, [handleOnChange, pan, props]);

  /**
   * Pan responder
   */
  const panResponder = useRef(
    PanResponder.create({
      onStartShouldSetPanResponder: () => true,
      onMoveShouldSetPanResponder: () => true,

      onPanResponderStart: (e, gs) => {
        setIsActive(true);
      },

      onPanResponderMove: (e, gs) => {
        let dist = props.horizontal ? gs.dx : gs.dy;
        dist = Math.min(Math.max(dist, -props.length), props.length);

        pan.setValue(dist);
      },

      onPanResponderTerminate: (evt, gs) => {
        setIsActive(false);
        pan.setValue(0);
        props.onChange(0); // Trigger it without throttling to make sure it goes through
      },
      onPanResponderRelease: () => {
        setIsActive(false);
        pan.setValue(0);
        props.onChange(0); // Trigger it without throttling to make sure it goes through
      },
    })
  ).current;

  const height = props.horizontal ? 1.5 * props.breadth : 2 * props.length;
  const width = props.horizontal ? 2 * props.length : 1.5 * props.breadth;

  return (
    <View
      style={{
        height: height,
        width: width,
        borderRadius: props.length,
        backgroundColor: props.disabled ? 'lightgrey' : '#4b9ed6',
        justifyContent: 'center',
        alignItems: 'center',
        marginVertical: props.breadth / 2,
      }}
    >
      <Animated.View
        style={{
          transform: [
            props.horizontal ? { translateX: pan } : { translateY: pan },
          ],
        }}
        {...panResponder.panHandlers}
        pointerEvents={props.disabled ? 'none' : 'auto'}
      >
        <View
          style={{
            height: props.breadth,
            width: props.breadth,
            borderRadius: props.length / 2,
            backgroundColor: props.disabled
              ? colors.grey.dim
              : colors.primary[900],
          }}
        />
      </Animated.View>
    </View>
  );
};
